import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import AppLayout from "@/components/AppLayout";
import Icon from "@/components/Icon";
import ChartTooltip from "@/components/ChartTooltip";
import api from "@/lib/api";
import { destMeta } from "@/lib/qr";

const COLORS = ["#6366F1", "#10B981", "#06B6D4", "#F59E0B", "#F43F5E", "#8B5CF6"];

function Chart({ title, children }) { return (<div className="rounded-2xl border border-border bg-card p-5"><h3 className="font-display font-bold text-sm mb-4">{title}</h3>{children}</div>); }

export default function Analytics() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [qr, setQr] = useState(null);
  const [items, setItems] = useState([]);
  const [compareIds, setCompareIds] = useState([]);
  const [compareData, setCompareData] = useState(null);

  useEffect(() => {
    if (id) {
      api.get(`/qr/${id}`).then(({ data }) => setQr(data)).catch(() => {});
      api.get(`/qr/${id}/analytics`, { params: { days } }).then(({ data }) => setData(data)).catch(() => {});
    } else {
      api.get("/analytics/overview", { params: { days } }).then(({ data }) => setData(data)).catch(() => {});
      api.get("/qr").then(({ data }) => setItems(data)).catch(() => {});
    }
  }, [id, days]);

  useEffect(() => {
    if (id || compareIds.length < 2) { setCompareData(null); return; }
    api.get("/analytics/compare", { params: { ids: compareIds.join(","), days } }).then(({ data }) => setCompareData(data)).catch(() => {});
  }, [id, compareIds, days]);

  const toggleCompare = (qid) => setCompareIds((p) => (p.includes(qid) ? p.filter((x) => x !== qid) : p.length >= 6 ? p : [...p, qid]));

  const exportCsv = () => {
    if (!data?.series) return;
    const rows = [["date", "scans", "unique"], ...data.series.map((s) => [s.date, s.scans, s.unique])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" }); const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "qr-code-creator-studio-analytics.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <AppLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          {id && <button onClick={() => nav("/app")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1"><Icon name="ArrowLeft" className="h-4 w-4" />Back</button>}
          <h1 className="text-2xl font-display font-extrabold">{id && qr ? qr.name : "Analytics"}</h1>
          <p className="text-sm text-muted-foreground">{id && qr ? `${destMeta(qr.destination_type).name} · ${qr.mode}` : "Scan performance across your workspace."}</p>
        </div>
        <div className="flex gap-2">
          <select value={days} onChange={(e) => setDays(+e.target.value)} data-testid="analytics-range" className="rounded-xl border border-input bg-background px-3 py-2 text-sm"><option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option></select>
          <button data-testid="export-csv" onClick={exportCsv} className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-accent flex items-center gap-1.5"><Icon name="Download" className="h-4 w-4" />CSV</button>
        </div>
      </div>

      {data?.static ? (
        <div className="rounded-2xl border border-dashed border-border py-16 text-center">
          <Icon name="Lock" className="h-10 w-10 mx-auto text-muted-foreground" />
          <h3 className="font-display font-bold mt-3">Analytics unavailable for static codes</h3>
          <p className="text-sm text-muted-foreground mt-1">Static QR data lives in the image, so scans can't be measured. Switch to dynamic to track scans.</p>
        </div>
      ) : !data ? (
        <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[["Total scans", data.total_scans, "MousePointerClick"], ["Unique (est.)", data.unique_estimate, "Users"], ["Outbound clicks", data.clicks, "ExternalLink"], ["Bots filtered", data.bots_filtered, "ShieldCheck"]].map(([l, v, i]) => (
              <div key={l} className="rounded-2xl border border-border bg-card p-5"><div className="h-9 w-9 rounded-xl bg-primary/10 text-primary grid place-items-center mb-2"><Icon name={i} className="h-4 w-4" /></div><div className="text-2xl font-display font-extrabold">{v}</div><div className="text-xs text-muted-foreground">{l}</div></div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground -mt-2"><Icon name="Info" className="h-3 w-3 inline mr-1" />Scans = QR visits. Unique = de-duplicated per day (an estimate). Clicks = outbound link taps.</p>

          <Chart title="Scans over time">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={data.series}><CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(d) => d.slice(5)} /><YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} /><Tooltip content={<ChartTooltip />} /><Line type="monotone" dataKey="scans" stroke="#6366F1" strokeWidth={2.5} dot={false} /><Line type="monotone" dataKey="unique" stroke="#10B981" strokeWidth={2} dot={false} /></LineChart>
            </ResponsiveContainer>
          </Chart>

          <div className="grid md:grid-cols-2 gap-6">
            <Chart title="By device"><Donut data={data.by_device} /></Chart>
            <Chart title="By OS"><Donut data={data.by_os} /></Chart>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Chart title="Top countries">{data.by_country?.length ? <BarList data={data.by_country} /> : <Empty />}</Chart>
            <Chart title="Top cities">{data.by_city?.length ? <BarList data={data.by_city} /> : <Empty />}</Chart>
          </div>
          <Chart title="Top browsers">{data.by_browser?.length ? <BarList data={data.by_browser} /> : <Empty />}</Chart>

          {!id && (
            <div className="grid md:grid-cols-2 gap-6">
              <Chart title="Top performing QR codes">
                <div className="space-y-2">{(data.top_qrs || []).map((q, i) => (<div key={q.id} className="flex items-center gap-3"><span className="text-xs font-bold text-muted-foreground w-4">{i + 1}</span><div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{q.name}</div><div className="text-xs text-muted-foreground">{destMeta(q.type).name}</div></div><span className="text-sm font-bold">{q.scans}</span></div>))}{!(data.top_qrs || []).length && <Empty />}</div>
              </Chart>
              <Chart title="Scans by destination type"><BarList data={(data.by_type || []).map((t) => ({ name: destMeta(t.name).name, value: t.value }))} /></Chart>
            </div>
          )}

          {!id && (
            <Chart title="Compare QR codes (select 2–6)">
              <div className="flex flex-wrap gap-2 mb-4">
                {items.filter((it) => it.mode === "dynamic").slice(0, 20).map((it) => (
                  <button key={it.id} data-testid={`compare-toggle-${it.id}`} onClick={() => toggleCompare(it.id)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${compareIds.includes(it.id) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"}`}>{it.name}</button>
                ))}
                {!items.some((it) => it.mode === "dynamic") && <Empty />}
              </div>
              {compareData?.items?.length >= 2 ? (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={mergeSeries(compareData.items)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" /><XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(d) => d.slice(5)} /><YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} /><Tooltip content={<ChartTooltip />} />
                      {compareData.items.map((it, i) => <Line key={it.id} type="monotone" dataKey={it.id} name={it.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={false} />)}
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">{compareData.items.map((it, i) => <div key={it.id} className="rounded-xl border border-border p-3"><div className="text-xs font-semibold truncate flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />{it.name}</div><div className="text-lg font-display font-extrabold mt-1">{it.total}<span className="text-xs text-muted-foreground font-normal"> scans</span></div><div className="text-xs text-muted-foreground">{it.unique} unique · {it.clicks} clicks</div></div>)}</div>
                </>
              ) : <p className="text-sm text-muted-foreground text-center py-6">Select at least two dynamic QR codes to compare.</p>}
            </Chart>
          )}
        </div>
      )}
    </AppLayout>
  );
}

const Empty = () => <p className="text-sm text-muted-foreground py-8 text-center">No data yet.</p>;
function mergeSeries(items) {
  const map = {};
  items.forEach((it) => (it.series || []).forEach((p) => { map[p.date] = map[p.date] || { date: p.date }; map[p.date][it.id] = p.scans; }));
  return Object.values(map).sort((a, b) => a.date.localeCompare(b.date));
}
function Donut({ data }) {
  if (!data?.length) return <Empty />;
  return (<ResponsiveContainer width="100%" height={220}><PieChart><Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>{data.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip content={<ChartTooltip />} /></PieChart></ResponsiveContainer>);
}
function BarList({ data }) {
  if (!data?.length) return <Empty />;
  return (<ResponsiveContainer width="100%" height={220}><BarChart data={data} layout="vertical" margin={{ left: 20 }}><XAxis type="number" hide allowDecimals={false} /><YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={90} stroke="hsl(var(--muted-foreground))" /><Tooltip cursor={{ fill: "hsl(var(--accent))" }} content={<ChartTooltip />} /><Bar dataKey="value" fill="#6366F1" radius={[0, 6, 6, 0]} barSize={18} /></BarChart></ResponsiveContainer>);
}
